import _flatMap from 'lodash-es/flatMap';
import { Competence, Domain, Matiere } from '@teetsh/app/src/types/bo';
import { getLabelText } from '@teetsh/app/src/components/Bo/referentialLabelParser';

const cleanArray = arr => {
  if (!arr) {
    return [];
  }
  return arr.filter(elem => elem).filter(elem => elem !== '');
};

function normalizeString(str: string): string {
  const trimmedStr = str.trim();
  // if last char is a dot, remove it
  if (trimmedStr.endsWith('.')) {
    return trimmedStr.slice(0, -1);
  }
  return trimmedStr;
}

function tolerantIncludes(arr: string[], str: string): boolean {
  return arr.some(
    s => normalizeString(s) === normalizeString(getLabelText(str)),
  );
}

const getOtherChoicesOrResult = (
  filteredChoices: string[],
  notfilteredChoices: string[],
  userChoices: string[],
): string[] => {
  const cleanedChoices: string[] = cleanArray(userChoices);
  return cleanedChoices.length > 0
    ? notfilteredChoices.filter(c => !tolerantIncludes(cleanedChoices, c))
    : filteredChoices;
};

function getFieldsAsArray<T, R>(array: T[], attribute: string, init: R[]): R[] {
  return array.reduce((acc, num) => {
    const temp: R[] =
      cleanArray(num[attribute] as R[]).length > 0 ? num[attribute] : [];
    return [...acc, ...(temp || [])];
  }, init);
}

export const getChoices = (
  bo: Matiere[],
  matieres: string[] = [],
  domaines: string[] = [],
  competences: string[] = [],
  goals: string[] = [],
): {
  domaines: string[];
  competences: string[];
  objectifs: string[];
} => {
  const matieresFromBO: Matiere[] = bo.filter(m =>
    tolerantIncludes(matieres, m.nom),
  );
  const domainesNotFiltered: Domain[] = _flatMap(matieresFromBO, m =>
    m.domaines ? m.domaines : [],
  );

  const domainesFiltered: Domain[] = domainesNotFiltered.filter(d =>
    cleanArray(domaines).length > 0 ? tolerantIncludes(domaines, d.nom) : true,
  );

  const competencesFromMatiere = _flatMap(matieresFromBO, m =>
    m.competences ? m.competences : [],
  );

  const goalsFromMatiere = _flatMap(matieresFromBO, m =>
    m.objectifs ? m.objectifs : [],
  );

  const objectifsFromDomaines = getFieldsAsArray<Domain, string>(
    domainesFiltered,
    'objectifs',
    [],
  );

  const competencesFiltered = getFieldsAsArray<Domain, Competence>(
    domainesFiltered,
    'competences',
    competencesFromMatiere,
  ).filter(d =>
    cleanArray(competences).length > 0
      ? !tolerantIncludes(competences, d.nom)
      : true,
  );

  const matchingCompetences = getFieldsAsArray<Domain, Competence>(
    domainesFiltered,
    'competences',
    competencesFromMatiere,
  ).filter(d =>
    cleanArray(competences).length > 0
      ? tolerantIncludes(competences, d.nom)
      : true,
  );

  const objectifsFiltered = matchingCompetences
    .reduce(
      (acc, num) => {
        const temp = num.objectifs || [];
        return [...acc, ...temp];
      },
      [...objectifsFromDomaines, ...goalsFromMatiere],
    )
    .filter(g => !tolerantIncludes(goals, g));

  return {
    domaines: getOtherChoicesOrResult(
      domainesFiltered.map(d => d.nom),
      domainesNotFiltered.map(d => d.nom),
      domaines,
    ),
    competences: competencesFiltered.map(d => d.nom),
    objectifs: objectifsFiltered,
  };
};
